import { useState, useEffect } from 'react';
import { templates } from '../data/templates';
import type { AgentTemplate } from '../data/templates';
import AgentTemplateCard from '../components/AgentTemplateCard';
import SwarmVisualization from '../components/SwarmVisualization';

export default function TemplateLibrary() {
  const [selectedTemplate, setSelectedTemplate] = useState<AgentTemplate | null>(null);
  const [filter, setFilter] = useState('All');
  const [deploying, setDeploying] = useState(false);
  const [deployed, setDeployed] = useState(false);

  useEffect(() => {
    setDeployed(false);
    setDeploying(false);
  }, [selectedTemplate]);

  const categories = ['All', ...new Set(templates.map((t) => t.category))];
  const filtered = filter === 'All' ? templates : templates.filter((t) => t.category === filter);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Template Library</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Pre-built swarm configurations for common use cases</p>
      </div>

      <div className="flex gap-2">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
              filter === cat
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((template) => (
          <AgentTemplateCard
            key={template.id}
            template={template}
            onSelect={setSelectedTemplate}
          />
        ))}
      </div>

      {selectedTemplate && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-800 dark:text-gray-100">{selectedTemplate.name}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{selectedTemplate.description}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedTemplate(null)}
                className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Close
              </button>
              <button
                onClick={() => {
                  if (deploying || deployed) return;
                  setDeploying(true);
                  setTimeout(() => {
                    setDeploying(false);
                    setDeployed(true);
                  }, 2000);
                }}
                disabled={deploying}
                className={`px-4 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                  deployed
                    ? 'bg-green-600 text-white cursor-default'
                    : deploying
                    ? 'bg-purple-600 text-white opacity-75 cursor-not-allowed'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                {deploying ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Deploying...
                  </span>
                ) : deployed ? (
                  <span className="flex items-center gap-1">✓ Deployed</span>
                ) : (
                  'Deploy Swarm'
                )}
              </button>
            </div>
          </div>
          {deployed && (
            <div className="mx-4 mt-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-green-700 dark:text-green-300 text-sm">
              <span>Swarm '{selectedTemplate.name}' deployed successfully! View it in the </span>
              <a href="/swarm" className="font-medium underline hover:no-underline text-green-700 dark:text-green-300">
                Swarm Dashboard
              </a>
              .
            </div>
          )}
          <SwarmVisualization
            nodes={selectedTemplate.agents}
            links={selectedTemplate.connections.map((c) => ({ source: c.from, target: c.to, label: c.label }))}
            className="h-[400px]"
          />
        </div>
      )}
    </div>
  );
}
